import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { FormDialog } from './FormDialog';
import { AuthControllerService } from '../api/services/AuthControllerService';
import { accessTokenStore } from '../auth/accessTokenStore';
import { useToast } from '../hooks/useToast';
import { useLogout } from '../hooks/useLogout';
import { ApiError } from '../api/core/ApiError';

type ChangePasswordForm = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

const meetsPasswordPolicy = (value: string) => {
  if (!value) return false;
  const hasUpper = /[A-Z]/.test(value);
  const hasLower = /[a-z]/.test(value);
  const hasNumber = /\d/.test(value);
  const hasLength = value.length >= 10;
  return hasUpper && hasLower && hasNumber && hasLength;
};

export default function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['common']);
  const { showToast } = useToast();
  const logout = useLogout();

  const ClearPasswordsOnMount = ({ setValue }: { setValue: (name: keyof ChangePasswordForm, value: string) => void }) => {
    useEffect(() => {
      setValue('currentPassword', '');
      setValue('newPassword', '');
      setValue('confirmNewPassword', '');
    }, [setValue]);
    return null;
  };

  const handleSave = async (data: ChangePasswordForm) => {
    try {
      const token = accessTokenStore.get();
      await AuthControllerService.changePassword(
        token ? `Bearer ${token}` : '',
        {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        }
      );
      showToast('success', t('auth.change_password'), t('auth.passwordChangeSuccess'));
      onClose();
      await logout();
    } catch (error) {
      console.warn('Change password failed');
      let message = t('auth.passwordChangeFailed');
      if (error instanceof ApiError) {
        const errorCode = (error.body as any)?.errorCode;
        if (errorCode === 'AUTH_003') {
          message = t('auth.currentPasswordInvalid');
        }
      }
      if (message === t('auth.passwordChangeFailed') && typeof (error as any)?.message === 'string') {
        message = (error as any).message;
      }
      showToast('error', t('messages.error'), message);
    }
  };

  return (
    <FormDialog<ChangePasswordForm>
      value={{ currentPassword: '', newPassword: '', confirmNewPassword: '' }}
      onClose={onClose}
      onSave={handleSave}
      titleKey="auth.change_password"
      width="520px"
      formAutoComplete="off"
    >
      {({ register, formState, getValues, setValue }) => {
        return (
          <>
            <ClearPasswordsOnMount setValue={setValue} />
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('auth.currentPassword')} *
              </label>
              <input
                type="password"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                className="e-input w-full"
                {...register('currentPassword', { required: true })}
                onFocus={() => {
                  if (getValues('currentPassword')) {
                    setValue('currentPassword', '');
                  }
                }}
              />
              {formState.errors.currentPassword?.message && (
                <div className="text-red-500 text-sm mt-1">
                  {formState.errors.currentPassword.message}
                </div>
              )}
            </div>

            <div className="mb-1">
              <label className="block text-sm font-medium text-gray-700">
                {t('auth.newPassword')} *
              </label>
              <input
                type="password"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                className="e-input w-full"
                {...register('newPassword', {
                  required: true,
                  validate: (value) =>
                    meetsPasswordPolicy(value) || 'validation.password_policy',
                })}
                onFocus={() => {
                  if (getValues('newPassword')) {
                    setValue('newPassword', '');
                  }
                }}
              />
              {formState.errors.newPassword?.message && (
                <div className="text-red-500 text-sm mt-1">
                  {formState.errors.newPassword.message}
                </div>
              )}
            </div>

            <div className="mb-4 text-xs text-gray-500">
              {t('auth.passwordRequirements')}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('auth.confirmPassword')} *
              </label>
              <input
                type="password"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                className="e-input w-full"
                {...register('confirmNewPassword', {
                  required: true,
                  validate: (value) =>
                    value === getValues('newPassword') || 'validation.password_mismatch',
                })}
                onFocus={() => {
                  if (getValues('confirmNewPassword')) {
                    setValue('confirmNewPassword', '');
                  }
                }}
              />
              {formState.errors.confirmNewPassword?.message && (
                <div className="text-red-500 text-sm mt-1">
                  {formState.errors.confirmNewPassword.message}
                </div>
              )}
            </div>
          </>
        );
      }}
    </FormDialog>
  );
}
